import styled from 'styled-components';
import { ProfileImageUploadCard } from 'app/components/ProfileImageUploadCard';
import { GridLayout, GridLayoutItem } from '@progress/kendo-react-layout';
import { LabelMedium } from 'app/components/SHLabel';
import { capitalizeFirstLetter, monthDateYearFormat, useNavigateToRoute } from 'utils/common';
import { useLocation, useParams } from 'react-router-dom';
import { AdminEditIcon } from 'admin/assets/icon';
import { ShowRegistration } from 'services/openapi';
import { CardSkeletonLoader } from 'app/components/CardSkeletonLoader';
import { usePermission } from 'hooks/usePermission';
import useShowApi from 'admin/hooks/useShowApi';
import { SHToolTip } from 'app/components/SHToolTip';
import Cookies from 'js-cookie';

type Props = {
  showData: ShowRegistration | undefined;
  loading?: boolean;
};

export const ShowViewProfile = ({ showData, loading }: Props) => {
  const userType = Cookies.get('userType') ?? '';
  const redirect = useNavigateToRoute();
  const currentLocation = useLocation();
  const { hasViewPermission } = usePermission();
  const { handleDownloadJudgesCard } = useShowApi();
  const isPastDataFlag = currentLocation.pathname.includes('past-data');
  const { id: showId } = useParams();
  const handleEditClick = () => {
    redirect(userType === 'COACH' ? `/coach/shows/edit/${showId}` : `/admin/shows/edit/${showId}`);
  };
  const handleShowsDwoload = async () => {
    try {
      const url: any = await handleDownloadJudgesCard(showId as string);

      // Extract filename from URL
      const filename = url.split('/').pop();

      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename!; // Use the extracted filename

          // Append link to body (required for Firefox)
          document.body.appendChild(link);

          // Trigger download
          link.click();

          // Clean up
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });

      return url;
    } catch (error) {
      console.error('Error downloading team point report:', error);
      throw error;
    }
  };
  return (
    <StyledCommonPageWrapper>
      {loading ? (
        <CardSkeletonLoader
          isProfileImageLoader
          isHeaderLoader
          isTextLoader
          lineContent={3}
          headerWidth={300}
        />
      ) : (
        <ProfileImageUploadCard
          buttonLabel={hasViewPermission('0500101005') ? 'Edit' : undefined}
          buttonType="secondary"
          svgIcon={<AdminEditIcon />}
          onButtonClick={handleEditClick}
          className="ProfileImageUploadCard"
          imgSrc={showData?.imageUrl}
          isPastDataFlag={isPastDataFlag}
          isTopButton
          topButtonLabel="Download Judges Card"
          onTopButtonClick={handleShowsDwoload}
          isCoach={userType === 'COACH'}
        >
          <GridLayout
            gap={{ rows: 0, cols: 10 }}
            rows={[{ height: 'auto' }, { height: 'auto' }]}
            cols={[
              { width: '25%' },
              { width: '25%' },
              { width: '25%' },
              { width: '25%' },
            ]}
            className="profilecard-grid"
          >
            <GridLayoutItem row={1} col={1} colSpan={4} className="first-item">


              <h3 className="header-title back-number" >
                <SHToolTip position="top" className='tooltip-style'>
                  <span title={capitalizeFirstLetter(showData?.showName)}>{capitalizeFirstLetter(showData?.showName) ?? ''}</span>
                  <span title={showData?.season?.name}>({showData?.season?.name})</span>{' '}
                </SHToolTip>
              </h3>

            </GridLayoutItem>
          </GridLayout>
          <GridLayout
            gap={{ rows: 0, cols: 10 }}
            rows={[{ height: 'auto' }, { height: 'auto' }]}
            cols={[
              { width: '25%' },
              { width: '25%' },
              { width: '25%' },
              { width: '25%' },
            ]}
            className="profilecard-grid"
          >
            <GridLayoutItem
              row={2}
              col={1}
              colSpan={2}
              className="hosted-style"
            >
              <CustomLabelMedium color="#475467" className='info-style'>
                Hosted By :
                <SHToolTip position="top" className='tooltip-style'>
                  <span color="#9F0404" title={showData?.hostedBy ?? ""}>{showData?.hostedBy} </span>
                </SHToolTip>
              </CustomLabelMedium>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={1} colSpan={2}>
              <CustomLabelMedium color="#475467" className='info-style'>
                Date :
                <SHToolTip position="top" className='tooltip-style'>
                  <span title={monthDateYearFormat(showData?.showStartDate ?? "")}>{monthDateYearFormat(showData?.showStartDate)}</span>
                </SHToolTip>
              </CustomLabelMedium>
            </GridLayoutItem>
            <GridLayoutItem
              row={4}
              col={1}
              colSpan={2}
              className="location-style"
            >
              <CustomLabelMedium color="#475467" className='info-style'>
                Location :
                <SHToolTip position="top" className='tooltip-style'>
                  <span title={showData?.showLocation ?? ""}>{showData?.showLocation}</span>
                </SHToolTip>
              </CustomLabelMedium>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={3} colSpan={2} className="right-side">
              <CustomLabelMedium color="#475467" className='right-side-label-style'>
                Address :
                <SHToolTip position="top" className='tooltip-style'>
                  <span title={showData?.address ?? ""}>{showData?.address}</span>
                </SHToolTip>
              </CustomLabelMedium>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={3} colSpan={2} className="right-side">
              <CustomLabelMedium color="#475467"></CustomLabelMedium>
            </GridLayoutItem>
            <GridLayoutItem row={4} col={3} colSpan={2} className="right-side">
              <CustomLabelMedium color="#475467"></CustomLabelMedium>
            </GridLayoutItem>
          </GridLayout>
        </ProfileImageUploadCard>
      )
      }
    </StyledCommonPageWrapper >
  );
};

const StyledCommonPageWrapper = styled.div`
  .ProfileImageUploadCard {
    padding: 24px 40px 34px 40px !important;
    align-items: center;
    button {
      span {
        font-family: Inter;
        font-size: 16px;
        font-weight: 400;
        line-height: 19.36px;
      }
    }
    .image-upload {
      width: 122.11px;
      min-width: 122.11px !important;
      height: 122.11px;
    }
    .child-wrapper {
      width: calc(100% - 215px);
      .first-item {
        padding-bottom: 17px;
      }
    }
    .hosted-style {
      span {
        color: #9f0404;
      }
    }
    .location-style {
      span {
        color: #0028ff;
      }
    }
    .field-style {
      border-color: #515e71;
    }
    &.CheckAfiliation {
      font-size: 13px !important;
      font-weight: 500 !important;
      color: #a3a3a3 !important;
    }
    .header-title {
      font-family: 'Inter';
      color: #515e71;
      margin: 0px;
      width: calc(100% - 160px);
      > div{
      width: 100%;
       .tooltip-style{
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          display: flex;
          span {
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                max-width: 50%;
          }
      }
    }
      span {
        font-family: Inter;
        font-size: 18px;
        font-weight: 500;
        color: #0028ff;
        margin-left: 10px;
        white-space: nowrap;
      }
    }
    .profilecard-grid {
      span {
        font-weight: 600;
      }
      .right-side {
        padding-left: 20px;
        border-left: 1px solid #a6b1dc;
        width: 100%;
        .right-side-label-style{
          width: 100%;
          > div{
            width: calc(100% - 65px);
            .tooltip-style{
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }
      .email-style {
        span {
          color: #0028ff;
        }
      }
    }
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    .ProfileImageUploadCard {
      button {
        span {
          font-family: Inter;
          font-size: 16px;
          font-weight: 400;
          line-height: 19.36px;
        }
      }
      .child-wrapper {
        width: 70%;
      }
    }
  }
  @media (min-width: 768px) and (max-width: 991px) {
    .ProfileImageUploadCard {
      flex-direction: column;
      padding: 20px !important;
      button {
        span {
          font-family: Inter;
          font-size: 16px;
          font-weight: 400;
          line-height: 19.36px;
        }
        position: unset !important;
        transform: unset !important;
      }
      .k-grid-layout {
        width: 100%;
      }
      .child-wrapper {
        width: 100% !important;
        padding: 0px;
        .first-item {
          grid-area: 1 / 1 / auto / span 4 !important;
        }
        .height-style {
          grid-area: 4 / 1 / auto / span 2 !important;
        }
        .weight-style {
          grid-area: 4 / 2 / auto / span 2 !important;
        }
        .email-style {
          grid-area: 4 / 1 / auto / span 2 !important;
        }
      }
    }
  }
  @media (min-width: 100px) and (max-width: 767px) {
    .ProfileImageUploadCard {
      flex-direction: column;
      padding: 20px !important;

      .right-side {
        padding-left: 0px !important;
        border-left: 0px solid !important;
      }
      button {
        span {
          font-family: Inter;
          font-size: 16px;
          font-weight: 400;
          line-height: 19.36px;
        }
        position: unset !important;
        transform: unset !important;
      }
      .k-grid-layout {
        display: flex;
        flex-direction: column;
        width: 100%;
      }
      .child-wrapper {
        width: 100% !important;
        padding: 0px;
        .first-item {
          grid-area: 1 / 1 / auto / span 4 !important;
        }
        .height-style {
          grid-area: 4 / 1 / auto / span 2 !important;
        }
        .weight-style {
          grid-area: 4 / 2 / auto / span 2 !important;
        }
        .email-style {
          grid-area: 4 / 3 / auto / span 2 !important;
        }
      }
    }
  }
`;
const CustomLabelMedium = styled(LabelMedium)`
  font-family: 'Inter';
  font-weight: 400;
  display: flex;
  padding: 1px 15px 1px 0;
  align-items: center;
  span {
    font-family: 'Inter';
    font-size: 14px;
    line-height: 16.94px;
    color: #475467;
    padding: 2px 0 0 5px;
  }
  &.info-style{
    width: 100%;
    > div{
      width: calc(100% - 78px);
       .tooltip-style{
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      }
    }
  }
`;
